import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { DataService, MonitorEndpointsService, BranchEndpointService, BranchQueue, Counter } from '../common/index';
import { CounterCustomizationAttributes } from '../common/_types/customizations.types';
import { NewTokenData, PusherService } from 'app/common/_services/pusher.service';
import { Monitor, MonitorRow, MonitorScreenRes, MonitorScreenViewType } from '../common/_types';
import { parseMonitor } from '../common/_utils/monitor.util';
import { showScreenLoadError } from '../common/_utils/api.util';
import { normalizeIdKey } from '../common/_utils/id.util';
import { tokenIntvertColorFlashAnimation, triggerFlash, onFlashDone } from '../common/common.animation';
import { findPendingItem, findServingItem, shouldPlayPendingSound } from './monitor.util';
import { DEFAULT_COUNTER_CUSTOMIZATION } from 'app/common/_constants/customization.constants';

@Component({
  selector: 'monitor',
  templateUrl: './monitor.component.html',
  providers: [MonitorEndpointsService],
  animations: [tokenIntvertColorFlashAnimation()],
})
export class MonitorComponent implements OnInit, OnDestroy {
  monitorData: Monitor;
  counters: Counter[] = [];
  queues: BranchQueue[] = [];
  monitorRows: MonitorRow[] = [];
  tokenDisplayCustomization: CounterCustomizationAttributes = DEFAULT_COUNTER_CUSTOMIZATION;
  counterLowCount = false;
  viewType: MonitorScreenViewType | '' = '';

  private monitorRowById: { [key: string]: MonitorRow } = {};

  get isVideoCampaign(): boolean {
    return this.monitorData?.mediaType === 'video';
  }

  private get items(): (Counter | BranchQueue)[] {
    return this.monitorData?.itemType === 'counter' ? this.counters : this.queues;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private monitorEndpointsService: MonitorEndpointsService,
    private pusherService: PusherService,
    public branchService: BranchEndpointService,
  ) {
    this.monitorData = parseMonitor();
  }

  ngOnInit() {
    this.loadMonitors();
    this.initPusherSubscription();
  }

  ngOnDestroy() {
    this.pusherService.disconnectAllChannels();
  }

  initPusherSubscription() {
    const branchId = this.route.snapshot.params['branchId'];
    this.pusherService.connectTokenBroadcast(branchId, this.handleNewTokenData);
  }

  navigateBasedOnViewType() {
    if (!this.monitorData?.id) return;
    const { viewType, id } = this.monitorData;
    const { branchId } = this.route.snapshot.params;
    this.router.navigate([`/branch/${branchId}/monitor/${id}/${viewType}`], { replaceUrl: true });
  }

  handleNewTokenData = (payload: NewTokenData) => {
    if (!payload) return;
    const { itemType, playTokenFetchSound, playSoundOnAllBranchQueues } = this.monitorData;
    const { attributes } = payload.data;
    const { status, tokenNumber } = attributes;

    if (status === 'pending' && playTokenFetchSound) {
      return this.playNewPendingTokenSound(payload);
    }

    if (status !== 'serving') return;

    const monitorItem = findServingItem(this.items, itemType, attributes);

    if (monitorItem) {
      this.onNewTokenServingHook(payload);
      this.triggerMonitorItemAnimation(`${monitorItem.id}`);
      this.branchService.playTokenEventSound('', tokenNumber);
    } else if (playSoundOnAllBranchQueues) {
      this.branchService.playTokenEventSound('', tokenNumber);
    }
  };

  triggerMonitorItemAnimation(itemId: string) {
    const row = this.monitorRowById[normalizeIdKey(itemId)];
    if (row) triggerFlash(row.flashItem);
  }

  loadMonitors() {
    const id = +this.route.snapshot.params['id'];
    this.monitorEndpointsService.getMonitorData(id).subscribe({
      next: (res) => this.onMonitorLoaded(res),
      error: (error) => this.onMonitorLoadError(error),
    });
  }

  private onMonitorLoaded(res: MonitorScreenRes) {
    const { data, included } = res;
    this.monitorData = parseMonitor(data, included);

    this.viewType = this.monitorData.viewType;
    this.counters = this.monitorData.counters;
    this.queues = this.monitorData.queues;

    if (this.viewType === 'token_display') this.loadTokenDisplayCustomization();

    this.navigateBasedOnViewType();
    this.onMonitorDataLoadedHook();
  }

  private onMonitorLoadError(error: any) {
    showScreenLoadError(error, () => {
      this.dataService.clearData();
      this.router.navigate(['login']);
    });
  }

  loadTokenDisplayCustomization() {
    this.monitorEndpointsService.getTokenDisplayCustomization().subscribe((res: any) => {
      this.tokenDisplayCustomization = res?.data?.attributes;
    });
  }

  playNewPendingTokenSound(payload: NewTokenData) {
    const { branchQueueId, totalPeopleInQueue } = payload?.data?.attributes || {};
    const { itemType, playSoundOnAllBranchQueues, playSoundBehaviourType } = this.monitorData;
    const { soundEffects } = this.branchService;

    const monitorItem = findPendingItem(this.items, itemType, branchQueueId);
    const isIdMatch = !!monitorItem || playSoundOnAllBranchQueues;
    const isCorrectBehaviour = shouldPlayPendingSound(playSoundBehaviourType, totalPeopleInQueue);

    if (isIdMatch && isCorrectBehaviour) {
      this.branchService.playTokenEventSound(soundEffects.newToken);
    }
  }

  refreshPage() {
    location.reload();
  }

  // hooks — default implementations; children may override and call super
  onMonitorDataLoadedHook() {
    this.buildMonitorRows();
    this.updateCounterLowCount();
  }

  onNewTokenServingHook(payload?: NewTokenData) {
    this.updateMonitorRowOnServing(payload);
  }

  protected buildMonitorRows() {
    if (this.monitorData?.itemType === 'counter') {
      this.monitorRows = this.monitorData?.enableGroupCounterByName
        ? this.buildGroupedCounterRows(this.counters || [])
        : this.buildCounterRows(this.counters || []);
    } else if (this.monitorData?.itemType === 'branch_queue') {
      this.monitorRows = this.buildQueueRows(this.queues || []);
    } else {
      this.monitorRows = [];
    }

    this.monitorRowById = this.indexRowsByItemId(this.monitorRows);
  }

  private buildCounterRows(counters: Counter[]): MonitorRow[] {
    return counters.map((counter) => ({
      name: counter.name,
      tokenNumber: counter?.currentlyServing?.tokenNumber || '000',
      itemIds: [counter.id],
      lastServingTokenId: +(counter?.currentlyServing?.id || 0),
      flashItem: { flag: false, state: 'original' },
    }));
  }

  private buildGroupedCounterRows(counters: Counter[]): MonitorRow[] {
    return this.groupCountersByName(counters).map(({ groupName, countersInGroup }) => {
      const latest = this.getLatestCounterByServingEvent(countersInGroup);
      return {
        name: groupName,
        tokenNumber: latest?.currentlyServing?.tokenNumber || '000',
        itemIds: countersInGroup.map((c) => c.id),
        lastServingTokenId: +(latest?.currentlyServing?.id || 0),
        flashItem: { flag: false, state: 'original' },
      };
    });
  }

  private buildQueueRows(queues: BranchQueue[]): MonitorRow[] {
    return queues.map((queue) => ({
      name: queue.name,
      tokenNumber: queue.currentToken?.tokenNumber || '000',
      itemIds: [queue.id],
      lastServingTokenId: +(queue.currentToken?.id || 0),
      flashItem: { flag: false, state: 'original' },
    }));
  }

  /** Map every item id (each counter/queue, including grouped members) to its row. */
  private indexRowsByItemId(rows: MonitorRow[]): { [key: string]: MonitorRow } {
    const index: { [key: string]: MonitorRow } = {};
    rows.forEach((row) => row.itemIds.forEach((id) => (index[normalizeIdKey(id)] = row)));
    return index;
  }

  // When enableGroupCounterByName is on, multiple counters with the same name are grouped into one row to support branches with multiple queues in the same physical location
  private groupCountersByName(counters: Counter[]): { groupName: string; countersInGroup: Counter[] }[] {
    const grouped: { [name: string]: Counter[] } = {};
    counters.forEach((counter) => {
      const key = counter.name || '';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(counter);
    });
    return Object.keys(grouped).map((groupName) => ({ groupName, countersInGroup: grouped[groupName] }));
  }

  private getLatestCounterByServingEvent(counters: Counter[]): Counter | null {
    return counters.reduce((latest, current) => {
      return +(current?.currentlyServing?.id || 0) > +(latest?.currentlyServing?.id || 0) ? current : latest;
    }, null);
  }

  isMonitorRowAnimating(row: MonitorRow): boolean {
    return row?.flashItem?.flag ?? false;
  }

  getMonitorRowAnimationState(row: MonitorRow): string {
    return row?.flashItem?.state ?? 'original';
  }

  onMonitorRowAnimationDone(row: MonitorRow) {
    if (row?.flashItem?.flag) onFlashDone(row.flashItem);
  }

  private updateCounterLowCount() {
    this.counterLowCount = this.monitorRows.length <= 4;
  }

  private updateMonitorRowOnServing(payload?: NewTokenData) {
    if (!payload) return;
    const { counterId, branchQueueId, tokenNumber } = payload.data?.attributes || {};

    const lookupId = this.monitorData?.itemType === 'counter' ? counterId : branchQueueId;
    if (lookupId == null) return;

    const row = this.monitorRowById[normalizeIdKey(lookupId)];
    if (!row) return;
    row.tokenNumber = tokenNumber || '000';
    row.lastServingTokenId = +(payload.data?.id || row.lastServingTokenId);
  }
}
